// @flow
import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import Modal from "./Modal";
import ContainerSection from "./ContainerSection";
import { ModalButtons, ModalTitle } from "./FilterModal";
import TextInput from "../../../components/lib/TextInput";
import Button from "../../../components/lib/Button";
import MarkupOverrideState, { MARKUP_MODE } from "../../../models/MarkupOverrideState";

type Props = {
  state: MarkupOverrideState,
};

const PILLS = [
  { value: MARKUP_MODE.SUGGESTED, label: "Pay Intel suggested", color: "#32429D" },
  { value: MARKUP_MODE.PAYROLL, label: "Payroll only", color: "#32429D" },
  { value: MARKUP_MODE.STAFFAUG, label: "Staff aug only", color: "#F16752" },
  { value: MARKUP_MODE.CUSTOM, label: "Custom — both", color: "#2C2C38" },
];

const HELPER_TEXT = {
  [MARKUP_MODE.SUGGESTED]: null,
  [MARKUP_MODE.PAYROLL]:
    "Optionally enter a custom payroll markup % to apply to every level in this PDF — for a client with a contracted rate that differs from what's stored.",
  [MARKUP_MODE.STAFFAUG]:
    "Optionally enter a custom staff aug markup % to apply to every level in this PDF — for a client with a contracted rate that differs from what's stored.",
  [MARKUP_MODE.CUSTOM]:
    "Optionally enter custom markup % values to apply to every level in this PDF — for a client with a contracted rate that differs from what's stored.",
};

const pillStyle = (color, active) => ({
  border: `1.5px solid ${active ? color : "#d9dce6"}`,
  color: active ? "#fff" : "#2C2C38",
  background: active ? color : "#fff",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  borderRadius: 999,
  cursor: "pointer",
});

const switchStyle = (on) => ({
  position: "relative",
  width: 38,
  height: 22,
  flexShrink: 0,
  borderRadius: 999,
  background: on ? "#32429D" : "#d9dce6",
  cursor: "pointer",
  transition: "background 0.15s ease",
});

const switchKnobStyle = (on) => ({
  position: "absolute",
  top: 2,
  left: on ? 18 : 2,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: "#fff",
  transition: "left 0.15s ease",
  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
});

const MarkupOverrideModal = ({ state }: Props) => (
  <Modal show={state.modalState.show} onHide={state.onCancel} innerStyle={{ width: 600 }}>
    <ContainerSection className="header">
      <ModalTitle>Download PDF</ModalTitle>
    </ContainerSection>

    {state.loading ? (
      <ContainerSection>
        <p className="text-muted text-x-small">Loading rate card data…</p>
      </ContainerSection>
    ) : (
      <ContainerSection>
        {state.hasNonUsdCurrency && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#F1F1F1",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 22,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#2C2C38", margin: "0 0 2px" }}>
                Convert to USD
              </p>
              <p style={{ fontSize: 12, color: "#6d7086", margin: 0 }}>
                Some rates in this export aren't in USD.{" "}
                {state.convertToUsd
                  ? "Converting them to USD in this PDF."
                  : "Turn on to convert them to USD."}
              </p>
            </div>
            <div
              role="switch"
              aria-checked={state.convertToUsd}
              style={switchStyle(state.convertToUsd)}
              onClick={state.toggleCurrency}
            >
              <div style={switchKnobStyle(state.convertToUsd)} />
            </div>
          </div>
        )}

        <p style={{ fontSize: 13, fontWeight: 600, color: "#6d7086", margin: "0 0 10px" }}>
          Markup basis
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
          {PILLS.map((pill) => (
            <button
              key={pill.value}
              type="button"
              style={pillStyle(pill.color, state.mode === pill.value)}
              onClick={() => state.setMode(pill.value)}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div style={{ minHeight: 162 }}>
          {state.mode === MARKUP_MODE.SUGGESTED ? (
            <div
              style={{
                background: "#F1F1F1",
                borderRadius: 7,
                padding: "12px 14px",
                fontSize: 13,
                color: "#6d7086",
                marginBottom: 18,
              }}
            >
              Uses Pay Intel's suggested markup for every level — no field to fill in.
            </div>
          ) : (
            <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "#9b9eae", margin: "0 0 16px" }}>
              {HELPER_TEXT[state.mode]}
            </p>
          )}

          {(state.mode === MARKUP_MODE.PAYROLL || state.mode === MARKUP_MODE.CUSTOM) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <label
                htmlFor="markup-override-payroll"
                style={{ margin: 0, whiteSpace: "nowrap", fontWeight: 600, color: "#2C2C38" }}
              >
                Payroll markup %
              </label>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  id="markup-override-payroll"
                  style={{ width: "100%" }}
                  type="number"
                  min="0"
                  placeholder="e.g. 18"
                  value={state.payrollInput}
                  onChange={state.onChangePayrollInput}
                />
              </div>
            </div>
          )}

          {(state.mode === MARKUP_MODE.STAFFAUG || state.mode === MARKUP_MODE.CUSTOM) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <label
                htmlFor="markup-override-staffing"
                style={{ margin: 0, whiteSpace: "nowrap", fontWeight: 600, color: "#2C2C38" }}
              >
                Staff aug markup %
              </label>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextInput
                  id="markup-override-staffing"
                  style={{ width: "100%" }}
                  type="number"
                  min="0"
                  placeholder="e.g. 45"
                  value={state.staffingInput}
                  onChange={state.onChangeStaffingInput}
                />
              </div>
            </div>
          )}
        </div>

        {state.error && (
          <p className="text-danger text-x-small" style={{ marginTop: 8 }}>
            {state.error}
          </p>
        )}
      </ContainerSection>
    )}

    <ContainerSection className="footer">
      <ModalButtons>
        <Button color="brand" size="large" disabled={state.loading} onClick={state.onConfirm}>
          Generate PDF
        </Button>
        <Button size="large" onClick={state.onCancel}>
          Cancel
        </Button>
      </ModalButtons>
    </ContainerSection>
  </Modal>
);

MarkupOverrideModal.propTypes = {
  state: PropTypes.object.isRequired,
};

export default observer(MarkupOverrideModal);
